/**
 * `apm-file:{uuid}` — ảnh trong tài liệu `.md` cá nhân lưu **id**, không lưu URL
 * (docs/plan-my-files.md §2.5): presigned URL hết hạn sau 60 phút, còn id thì
 * không. Dùng chung cho khung xem trước (APM-MYF-d) và hộp soạn (APM-MYF-f).
 *
 * Mọi phép thay đều chạy trên **markdown**, tức trước DOMPurify — DOMPurify gỡ
 * giao thức lạ `apm-file:` khỏi `src`.
 */
const APM_FILE = /apm-file:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Đích giả cho ảnh không còn đọc được — DOMPurify giữ nó (đường tương đối). */
export const APM_FILE_MISSING = '#apm-missing';

/**
 * Dấu id gắn vào **fragment** của URL khi mở trong editor. Fragment không gửi
 * lên S3 nên chữ ký không đổi, và id đi qua `marked` + DOMPurify nguyên vẹn —
 * không phải tra ngược URL → id (URL có thể bị `marked` mã hoá lại).
 */
const ID_MARK = 'apm-file=';

export function isApmFileId(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID.test(value);
}

export function apmFileIdsOf(markdown: string): string[] {
  return [...new Set([...markdown.matchAll(APM_FILE)].map((match) => match[1]!.toLowerCase()))];
}

/**
 * Thay `apm-file:{id}` bằng URL đã ký; id không đọc được ⇒ `APM_FILE_MISSING`.
 *
 * `markIds` (editor): URL mang thêm `#apm-file={id}`, ảnh mất mang
 * `#apm-missing:{id}` — để `markApmFileImages` gắn lại `data-file-id` và lần
 * lưu sau không đánh rơi tham chiếu của một ảnh chỉ tạm thời không đọc được.
 */
export function resolveApmFiles(
  markdown: string,
  urls: Readonly<Record<string, string | null>>,
  { markIds = false }: { readonly markIds?: boolean } = {},
): string {
  return markdown.replace(APM_FILE, (_match, raw: string) => {
    const id = raw.toLowerCase();
    const url = urls[id];
    if (!markIds) return url ?? APM_FILE_MISSING;
    return url ? `${url.split('#')[0]}#${ID_MARK}${id}` : `${APM_FILE_MISSING}:${id}`;
  });
}

/**
 * Sau `markdownToHtml(resolveApmFiles(md, urls, { markIds: true }), { images: true })`:
 * đọc id từ fragment, gắn `data-file-id`, bỏ fragment khỏi `src`.
 */
export function markApmFileImages(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  for (const image of template.content.querySelectorAll('img')) {
    const src = image.getAttribute('src') ?? '';
    const hash = src.indexOf('#');
    const fragment = hash >= 0 ? src.slice(hash + 1) : '';
    const id = fragment.startsWith(ID_MARK)
      ? fragment.slice(ID_MARK.length)
      : fragment.startsWith(`${APM_FILE_MISSING.slice(1)}:`)
        ? fragment.slice(APM_FILE_MISSING.length)
        : null;
    if (!isApmFileId(id)) continue;
    image.setAttribute('data-file-id', id.toLowerCase());
    if (fragment.startsWith(ID_MARK)) image.setAttribute('src', src.slice(0, hash));
  }
  return template.innerHTML;
}
